/**
 * Validation utility functions and common validation patterns
 */

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  // ponytail: method-shorthand (not arrow property) so TS checks the params
  // bivariantly — lets callers narrow to `(value: string)` / `(value: number)`
  // (incl. the frozen test fixtures) without reintroducing `any`.
  custom?(_value: unknown, _allValues?: Record<string, unknown>): string | null;
}

/**
 * Common validation patterns
 */
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  username: /^[a-zA-Z0-9_]{3,20}$/,
  phone: /^\+?[\d\s\-()]{10,}$/,
  url: /^https?:\/\/.+/,
} as const;

/**
 * Common error messages
 */
export const validationMessages = {
  required: (field: string) => `${field} is required`,
  minLength: (field: string, min: number) => `${field} must be at least ${min} characters long`,
  maxLength: (field: string, max: number) => `${field} must be no more than ${max} characters long`,
  email: "Please enter a valid email address",
  password:
    "Password must contain at least 8 characters, including uppercase, lowercase, and a number",
  username: "Username must be 3-20 characters and contain only letters, numbers, and underscores",
  phone: "Please enter a valid phone number",
  url: "Please enter a valid URL",
  match: (field: string) => `${field} do not match`,
} as const;

/**
 * Validates a single field value against a validation rule
 */
export const validateField = (
  value: unknown,
  rule: ValidationRule,
  fieldName?: string
): string | null => {
  // Required validation
  if (rule.required && (!value || (typeof value === "string" && !value.trim()))) {
    return fieldName ? validationMessages.required(fieldName) : "This field is required";
  }

  // Skip other validations if value is empty and not required
  if (!value || (typeof value === "string" && !value.trim())) {
    return null;
  }

  const stringValue = String(value);

  // Min length validation
  if (rule.minLength && stringValue.length < rule.minLength) {
    return fieldName
      ? validationMessages.minLength(fieldName, rule.minLength)
      : `Must be at least ${rule.minLength} characters long`;
  }

  // Max length validation
  if (rule.maxLength && stringValue.length > rule.maxLength) {
    return fieldName
      ? validationMessages.maxLength(fieldName, rule.maxLength)
      : `Must be no more than ${rule.maxLength} characters long`;
  }

  // Pattern validation
  if (rule.pattern && !rule.pattern.test(stringValue)) {
    // Try to provide a helpful message based on common patterns
    if (rule.pattern === validationPatterns.email) {
      return validationMessages.email;
    }
    if (rule.pattern === validationPatterns.password) {
      return validationMessages.password;
    }
    if (rule.pattern === validationPatterns.username) {
      return validationMessages.username;
    }
    if (rule.pattern === validationPatterns.phone) {
      return validationMessages.phone;
    }
    if (rule.pattern === validationPatterns.url) {
      return validationMessages.url;
    }
    return fieldName ? `${fieldName} format is invalid` : "Invalid format";
  }

  // Custom validation
  if (rule.custom) {
    return rule.custom(value);
  }

  return null;
};

/**
 * Common validation rules for reuse
 */
export const commonValidationRules = {
  required: { required: true },
  email: { required: true, pattern: validationPatterns.email },
  password: { required: true, minLength: 6 },
  confirmPassword: (_originalValue: unknown) => ({
    required: true,
    custom: (_value: unknown) =>
      _value !== _originalValue ? validationMessages.match("Passwords") : null,
  }),
  username: { required: true, minLength: 3, maxLength: 20, pattern: validationPatterns.username },
  name: { required: true, minLength: 1, maxLength: 100 },
  description: { maxLength: 1000 },
  url: { pattern: validationPatterns.url },
} as const;
